// External Imports
import { useState, useContext, useEffect } from "react";
import { MdAdd, MdRefresh } from "react-icons/md";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import ChargingCard from "./ChargingCard";
import Heading from "../UI/Heading";
import { useLocation } from "react-router-dom";
import { requestAdminAPI } from "../../api/posh-api";
import PaginationMenu from "../UI/PaginationMenu";
import DateFilter from "../UI/DateFilter";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import VehicleSelector from "../vehicles-page/VehicleSelector";
import UploadChargingModal from "./UploadChargingModal";
import ExportCsvButton from "../UI/ExportCsvButton";

/** ========================================================================
 * * EV Charging Page
 * Renders a page, which displays all EV charging records.
 * ========================================================================== */

export default function Charging() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    vin: "",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [chargings, setChargings] = useState([]);
  const [limit, setLimit] = useState(50);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);
  const [upload, setUpload] = useState(false);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      let filters = [];
      filters.push(`vin: { contains: "${filter.vin}" }`);
      if (filter.sd && filter.ed) {
        filters.push(
          `date: {ge: "${filter.sd}"}`,
          `date: {le: "${filter.ed}"}`
        );
      }
      const data = await requestAdminAPI("filter", {
        table: "charging",
        limit,
        filters,
        columns: ["vehicle { vin, year, model, color }"],
      });

      // Replace URL
      let url = "/charging?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setChargings(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Calculate Total
  let total = 0;
  chargings.forEach((charging) => {
    total += Number(charging.total);
  });

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter, limit]);

  return (
    <div className="container grid text-sm">
      <UploadChargingModal open={upload} setOpen={setUpload} />
      <Heading
        title="EV Charging"
        isBackArrow={true}
        text="Records are stored in the database, but need to updated manually by clicking the '+' icon on the top-right 
        and uploading a .csv file from the Tesla app. Check the month of the last record and download records from that month onwards."
      >
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <MdAdd
          onClick={() => setUpload(true)}
          className="text-3xl icon"
          title="Add Chargings"
        />
        <MdRefresh
          onClick={fetchRecords}
          className="icon"
          title="Refresh"
        />
        <ExportCsvButton array={chargings} filename="charging" />
      </Heading>

      <VehicleSelector
        selected={filter.vin}
        onSelect={(v) => setFilter({ ...filter, vin: v.vin })}
      />

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : chargings.length > 0 ? (
        <div className="list">
          {chargings.map((charge) => (
            <ChargingCard key={charge.id} charging={charge} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={limit}
        setLimit={setLimit}
        loading={loading}
        count={chargings.length}
      >
        <p className="text-stone-400">
          Total:{" "}
          <span className="font-semibold text-stone-200">
            ${total.toFixed(2)}
          </span>
        </p>
      </PaginationMenu>
    </div>
  );
}
